import { CLASS_NAMES, EVENTS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for handling body background updates based on `.featured-news-stories`
 */
export default class BodyBackgroundObserver extends MessageBus {
  /**
   * Constructor function for the BodyBackgroundObserver class.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    this.element = element;

    // Track last scroll position
    this.lastScrollY = window.scrollY;
    this.isClassAdded = false;

    // Initialize the observer
    this.init();
  }

  /**
   * Initializes the observer by setting up DOM references and event listeners.
   * @returns {object} BodyBackgroundObserver A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupObserver().bindEvents();
    return this;
  }

  /**
   * Cache DOM References
   * @returns {object} BodyBackgroundObserver A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.featuredNewsSection = document.querySelector(
      `.${CLASS_NAMES.FEATURED_NEWS_STORIES}`
    );
    this.bodyElement = document.body;
    return this;
  }

  /**
   * Sets up the Intersection Observer to track visibility of `.featured-news-stories`.
   * @returns {object} BodyBackgroundObserver A reference to the current instance of the class
   * @chainable
   */
  setupObserver() {
    if (!this.featuredNewsSection) return this;

    const observerOptions = {
      root: null,
      threshold: 0.1
    };

    this.observer = new IntersectionObserver(
      this.handleIntersect.bind(this),
      observerOptions
    );
    this.observer.observe(this.featuredNewsSection);

    return this;
  }

  /**
   * Bind scroll event to track user scrolling behavior
   * @returns {object} BodyBackgroundObserver A reference to the current instance of the class
   * @chainable
   */
  bindEvents() {
    this.handleResize();
    window.addEventListener(EVENTS.SCROLL, this.handleScroll.bind(this));
    window.addEventListener(EVENTS.RESIZE, this.handleResize.bind(this));
    window.addEventListener(EVENTS.SCROLL, this.handleResize.bind(this));
    window.addEventListener(EVENTS.LOAD, this.handleResize.bind(this));
    return this;
  }

  /**
   * Handles section visibility when scrolling.
   * @param {IntersectionObserverEntry[]} entries - The observer entries
   */
  handleIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.bodyElement.classList.add(CLASS_NAMES.BACKGROUND_ADDED);
        this.isClassAdded = true;
      }
    });
  }

  /**
   * Handles scroll behavior to remove the class when user scrolls up past `.featured-news-stories`.
   */
  handleScroll() {
    const currentScrollY = window.scrollY;
    if (this.isClassAdded) {
      const sectionRect = this.featuredNewsSection.getBoundingClientRect();
      if (sectionRect.top > window.innerHeight) {
        this.bodyElement.classList.remove(CLASS_NAMES.BACKGROUND_ADDED);
        this.isClassAdded = false;
      }
    }

    this.lastScrollY = currentScrollY;
  }

  handleResize() {
    const bodyHeight =
      document.offsetHeight || document.documentElement.offsetHeight || 0;
    document.body.style.setProperty('--height-body', `${bodyHeight}px`);

    return this;
  }
}
